import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

const CartContext = createContext(null);

const STORAGE = "sandar_cart";
const STORE_STORAGE = "sandar_pickup_store";

export function CartProvider({ children }) {
  const [storeId, setStoreIdState] = useState(() => localStorage.getItem(STORE_STORAGE) || "");
  const setStoreId = useCallback((id) => {
    setStoreIdState(id || "");
    if (id) localStorage.setItem(STORE_STORAGE, id); else localStorage.removeItem(STORE_STORAGE);
  }, []);
  const [items, setItems] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE);
      return raw ? JSON.parse(raw) : [];
    } catch { return []; }
  });

  useEffect(() => {
    localStorage.setItem(STORAGE, JSON.stringify(items));
  }, [items]);

  const addItem = useCallback((item, qty = 1, platter = null) => {
    setItems((prev) => {
      if (!platter) {
        const found = prev.find((p) => p.item_id === item.id && !p.platter);
        if (found) {
          return prev.map((p) => p.line_id === found.line_id
            ? { ...p, quantity: p.quantity + qty }
            : p);
        }
      }
      return [...prev, {
        line_id: platter ? `${item.id}-${Date.now()}-${Math.random().toString(16).slice(2)}` : item.id,
        item_id: item.id,
        name: item.name,
        price: platter ? item.price + platter.extra_total : item.price,
        quantity: qty,
        image_url: item.image_url,
        platter,
      }];
    });
  }, []);

  const removeItem = useCallback((line_id) => {
    setItems((prev) => prev.filter((p) => p.line_id !== line_id));
  }, []);

  const updateQty = useCallback((line_id, quantity) => {
    if (quantity <= 0) return removeItem(line_id);
    setItems((prev) => prev.map((p) => p.line_id === line_id ? { ...p, quantity } : p));
  }, [removeItem]);

  const updatePlatter = useCallback((line_id, item, platter) => {
    setItems((prev) => prev.map((line) => line.line_id === line_id ? {
      ...line,
      name: item.name,
      price: item.price + platter.extra_total,
      image_url: item.image_url,
      platter,
    } : line));
  }, []);

  const clear = useCallback(() => setItems([]), []);

  const subtotal = useMemo(() => items.reduce((s, i) => s + i.price * i.quantity, 0), [items]);
  const count = useMemo(() => items.reduce((s, i) => s + i.quantity, 0), [items]);

  const value = useMemo(
    () => ({ items, addItem, updateQty, updatePlatter, removeItem, clear, subtotal, count, storeId, setStoreId }),
    [items, addItem, updateQty, updatePlatter, removeItem, clear, subtotal, count, storeId, setStoreId]
  );

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
}

export const useCart = () => useContext(CartContext);
